/**
 * Where a visitor came from, when they arrived through the "Built by Ownly Digital"
 * credit in the footer of a site we built.
 *
 * Every one of those links carries `?ref=<site>`, so this is what turns "someone
 * visited" into "the DFC Experience sent us someone", and, when they go on to fill the
 * quiz in, into "that enquiry came from DFC's site".
 *
 * NOTHING IS STORED ON THE VISITOR'S DEVICE, and that is deliberate: no cookie, no
 * localStorage, no sessionStorage. Our privacy policy says this site works without
 * non-essential tracking cookies, and that promise is worth more than perfect
 * attribution. The tag is held in memory for the life of the tab (see
 * `rememberSiteRef`), which needs no consent banner and disappears the moment the tab
 * does. A visitor who reloads the page is simply no longer attributed, and that is the
 * right trade.
 */

/** Longest tag we will accept. Ours are short slugs like "dfc" or "proflow-physiques". */
const MAX_LENGTH = 40;

/**
 * Reduce a raw `?ref=` value to a safe slug, or null.
 *
 * This value reaches an email and a lead record, so it is allow-listed rather than
 * escaped: lowercase letters, digits and hyphens, nothing else. Anything a stranger
 * puts in the query string that does not look like one of our tags is dropped.
 */
export function cleanSiteRef(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase().slice(0, MAX_LENGTH);
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) ? value : null;
}

/**
 * Plain-English names for the tags we ship, so a notification reads "came from The DFC
 * Experience" rather than "came from dfc". An unknown tag falls back to itself: a new
 * site missing from this list still attributes correctly, it just reads as its slug.
 */
const SITE_NAMES: Record<string, string> = {
  dfc: "The DFC Experience",
  "proflow-academy": "ProFlow Academy",
  "proflow-academy-app": "the ProFlow Academy app",
  "proflow-physiques": "ProFlow Physiques",
  "proflow-physiques-app": "the ProFlow Physiques app",
  "elevate-physique": "Elevate Physique Coaching",
  growthsync: "GrowthSync",
  "my-trusted-accountant": "My Trusted Accountant",
  "fathom-coffee": "Fathom Coffee",
  "clarity-dental": "Clarity Dental",
  "happy-hounds": "Happy Hounds",
  vantor: "Vantor Studio",
  aurelia: "Aurelia Aesthetics",
  spotblue: "Spot Blue",
  fia: "Fitness Industry Accountants",
  store: "the store template",
};

export function siteRefName(ref: string): string {
  return SITE_NAMES[ref] ?? ref;
}

/* ------------------------------------------------------------------ in-memory tab state */

let refForThisTab: string | null = null;

/** Remembers the tag for as long as this tab is open. Memory only, never the device. */
export function rememberSiteRef(ref: string): void {
  refForThisTab = ref;
}

/** The tag this visitor arrived with, if they are still in the same tab. */
export function currentSiteRef(): string | null {
  return refForThisTab;
}
